package kz.dentalcrm.app.screens.services.add

import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.error_message
import dentalcrm.composeapp.generated.resources.snackbar_service_added
import kotlinx.collections.immutable.toPersistentList
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.base.Selectable
import kz.dentalcrm.app.core.utils.Result
import kz.dentalcrm.app.data.network.request_model.AddServiceRequest
import kz.dentalcrm.app.data.network.response_model.services_category.ServiceCategory
import kz.dentalcrm.app.domain.ServicesRepository
import kz.dentalcrm.app.screens.services.add.mvi.AddServiceSideEffect
import kz.dentalcrm.app.screens.services.add.mvi.AddServiceState
import kz.dentalcrm.app.screens.services.add.mvi.AddServiceViewEvent
import kz.dentalcrm.app.theme.SnackbarController
import kz.dentalcrm.app.theme.SnackbarEvent
import org.jetbrains.compose.resources.getString
import org.koin.core.component.inject

class AddServiceViewModel : BaseStateViewModel<
        AddServiceViewEvent, AddServiceState, AddServiceSideEffect>() {

    private val serviceRepository: ServicesRepository by inject()

    private val _isLoading = MutableStateFlow(false)
    val loading: StateFlow<Boolean> = _isLoading

    init {
        if (viewState.value.categories.isEmpty()) {
            getServiceCategories()
        }
    }

    private fun getServiceCategories() {
        setState {
            copy(
                screenState = AddServiceState.ScreenState.Loading
            )
        }
        launch(Dispatchers.Default) {
            when (val result = serviceRepository.getServiceCategories()) {
                is Result.Success -> {
                    result.value?.let { data ->
                        setState {
                            copy(
                                categories = data.map {
                                    Selectable(
                                        selected = false,
                                        data = it
                                    )
                                }.toPersistentList()
                            )
                        }
                    }
                }

                is Result.Error -> {

                }

                is Result.Failure -> {

                }
            }
            setState {
                copy(
                    screenState = AddServiceState.ScreenState.Idle
                )
            }
        }
    }

    private fun addService(
        name: String,
        description: String,
        duration: Int,
        price: Int,
        category: ServiceCategory
    ) {
        setState {
            copy(
                screenState = AddServiceState.ScreenState.Adding
            )
        }
        launch(Dispatchers.Default) {
            when (val result = serviceRepository.addService(
                request = AddServiceRequest(
                    category_id = category.id.toString(),
                    description = description,
                    duration = duration,
                    max_price = price,
                    name = name,
                    price = price,
                )
            )) {
                is Result.Success -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_service_added), SnackbarEvent.SnackbarType.Green))
                    setState {
                        copy(
                            screenState = AddServiceState.ScreenState.Added
                        )
                    }
                }

                is Result.Error -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(
                            screenState = AddServiceState.ScreenState.Error(
                                error = result.message
                                    ?: "Неизвестная ошибка (Проверьте введенные данные)"
                            )
                        )
                    }
                }

                else -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(
                            screenState = AddServiceState.ScreenState.Error(
                                error = "Неизвестная ошибка (Проверьте введенные данные)"
                            )
                        )
                    }
                }
            }
        }
    }

    override fun setInitialState() = AddServiceState()

    override fun handleEvents(event: AddServiceViewEvent) {
        when (event) {
            is AddServiceViewEvent.AddService -> {
                addService(
                    name = event.naming,
                    description = event.description,
                    duration = event.duration.toInt(),
                    price = event.price.toInt(),
                    category = viewState.value.categories.first { it.selected }.data
                )
            }

            is AddServiceViewEvent.CategorySelected -> {
                val selections = viewState.value.categories.toMutableList().map {
                    Selectable(
                        selected = it.data == event.category,
                        data = it.data
                    )
                }
                setState {
                    copy(
                        categories = selections.toPersistentList()
                    )
                }
            }
        }
    }

    override fun onCleared() {
        setState { AddServiceState() }
        _isLoading.value = false
    }
}